import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { NumericFormat } from 'react-number-format';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { decodeToken } from 'react-jwt';
import {
  FaFileAlt, FaSearch, FaUser, FaCar, FaPrint, FaTimes, FaCheckCircle, FaArrowLeft, FaMapMarkerAlt,
  FaBold, FaItalic, FaUnderline, FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify,
  FaUndo, FaRedo, FaEraser, FaSpinner, FaPlus
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import { sanitizarHtml } from '../others/sanitizarHtml';
import ConfirmModal from '../components/ConfirmModal';
import BarraCadastro from '../components/BarraCadastro';
import ImportarDocumentoVeiculo from '../components/ImportarDocumentoVeiculo';
import { consultarCnpj, completarComDadosCnpj, avisoCompletarCnpj } from '../others/consultaCnpj';
import { buscarVeiculo, salvarVeiculo } from '../others/cadastroVeiculo';
import { situacaoCadastro, buscarClientePorDocumento, complementarCliente, criarCliente } from '../others/cadastroSync';
import {
  pessoaVazia, veiculoVazio, clienteParaPessoa, removerMascaraCPFCNPJ, formatarCpfCnpj, mascaraCpfCnpj, formatarCep, buscarCep,
  EnderecoCampos, PageBackground, Content, TitleRow, Title, Card, CardHeader, CardTitle, SubTitle, SearchButton,
  FormRow, InputGroup, Label, HintText, ErrorText, Input, EmptyState, ActionsRow, PrimaryButton, SecondaryButton,
  PreviewWrapper, PreviewToolbar, PrintButton, SelectField, ModelosGrid, ModeloCard, EditorToolbar, FerramentaBtn,
  Separador, PaperSheet, ModalOverlay, ModalBox, ModalHeader, ModalClose, ModalContent, SearchRow, ResultList, ResultItem
} from '../others/documentosBase';
import { MODELOS, MODELOS_POR_ID, GRUPOS, extrasIniciais, gerarDocumentoHtml } from '../others/documentosComplementares';

// Documentos Complementares (declarações e requerimentos de vistoria/DETRAN), trazidos do Truck Control.
// O documento é só gerado para imprimir ou salvar como PDF pelo navegador: nada do documento é gravado no
// sistema. O que é gravado são os dados do cliente (quando o usuário salva ou altera) e do veículo.

const getAuthHeader = () => {
  const token = localStorage.getItem('jwtToken');
  return { headers: { Authorization: `Bearer ${token}` } };
};

// Veículo do cadastro de veículos → formato do formulário
const veiculoDoCadastro = (v) => ({
  marcaModelo: v.marcaModelo || '',
  cor: v.cor || '',
  placa: v.placa || '',
  chassi: v.chassi || '',
  renavam: v.renavam || '',
  motor: v.motor || '',
  ano: v.anoFabricacaoModelo || ''
});

// Formulário → cadastro de veículos
const veiculoParaCadastro = (v) => ({
  placa: v.placa, chassi: v.chassi, renavam: v.renavam, motor: v.motor, marcaModelo: v.marcaModelo, cor: v.cor, anoFabricacaoModelo: v.ano
});

const placaCompleta = (p) => /^[A-Z]{3}\d[A-Z0-9]\d{2}$/.test((p || '').toUpperCase().replace(/[^A-Z0-9]/g, ''));

const CAMPOS_PESSOA = ['nome', 'cpfCnpj', 'rg', 'telefone', 'email', 'cep', 'logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf'];
const CAMPOS_VEICULO_SYNC = ['placa', 'chassi', 'renavam', 'motor', 'marcaModelo', 'cor', 'ano'];
const ROTULOS_CAMPO_PESSOA = {
  nome: 'nome', cpfCnpj: 'CPF/CNPJ', rg: 'RG', telefone: 'telefone', email: 'e-mail', cep: 'CEP',
  logradouro: 'logradouro', numero: 'número', complemento: 'complemento', bairro: 'bairro', cidade: 'cidade', uf: 'UF'
};
const ROTULOS_CAMPO_VEICULO = {
  placa: 'placa', chassi: 'chassi', renavam: 'RENAVAM', motor: 'motor', marcaModelo: 'marca/modelo', cor: 'cor', ano: 'ano'
};

// ─── Rota: lista de documentos ou o formulário do documento escolhido ─────────

const DocumentosComplementares = () => {
  const { tipo } = useParams();
  if (!tipo) return <ListaDocumentos />;
  const modelo = MODELOS_POR_ID[tipo];
  if (!modelo) return <Navigate to="/documentos-complementares" replace />;
  return <FormDocumento key={modelo.id} modelo={modelo} />;
};

// ─── Lista: cada documento é um item separado ─────────────────────────────────

const ListaDocumentos = () => {
  const navigate = useNavigate();
  const [filtro, setFiltro] = useState('');
  const termo = filtro.trim().toLowerCase();
  const visiveis = MODELOS.filter((m) => !termo || `${m.titulo} ${m.sub}`.toLowerCase().includes(termo));

  return (
    <PageBackground>
      <Header />
      <Content>
        <TitleRow>
          <Title><FaFileAlt size={22} /> Documentos Complementares</Title>
        </TitleRow>

        <Card>
          <InputGroup>
            <Label>Pesquisar documento</Label>
            <Input value={filtro} onChange={(ev) => setFiltro(ev.target.value)} placeholder="Ex.: chassi, plaqueta, baixa..." />
          </InputGroup>
        </Card>

        {visiveis.length === 0 && <EmptyState>Nenhum documento encontrado.</EmptyState>}

        {GRUPOS.map((grupo) => {
          const itens = visiveis.filter((m) => m.grupo === grupo);
          if (itens.length === 0) return null;
          return (
            <Card key={grupo}>
              <CardHeader>
                <CardTitle><FaFileAlt size={16} /> {grupo}</CardTitle>
              </CardHeader>
              <ModelosGrid>
                {itens.map((m) => (
                  <ModeloCard key={m.id} type="button" onClick={() => navigate(`/documentos-complementares/${m.id}`)}>
                    <strong>{m.titulo}</strong>
                    <span>{m.sub}</span>
                  </ModeloCard>
                ))}
              </ModelosGrid>
            </Card>
          );
        })}
      </Content>
    </PageBackground>
  );
};

// ─── Formulário de um documento ───────────────────────────────────────────────

const FormDocumento = ({ modelo }) => {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();
  const navigate = useNavigate();
  const previewRef = useRef(null);
  const editorRef = useRef(null);
  const editadoRef = useRef(false);
  const buscaPlacaRef = useRef(0);

  const [declarante, setDeclarante] = useState(pessoaVazia());
  const [comprador, setComprador] = useState(pessoaVazia());
  const [veiculo, setVeiculo] = useState(veiculoVazio());
  const [extras, setExtras] = useState(() => extrasIniciais(modelo));
  const [dataDocumento, setDataDocumento] = useState('');
  const [local, setLocal] = useState('');

  const [errosDeclarante, setErrosDeclarante] = useState({});
  const [errosComprador, setErrosComprador] = useState({});
  const [erroVeiculo, setErroVeiculo] = useState('');

  // Snapshot do que está gravado no cadastro, pra comparar com o que está preenchido aqui
  // e mostrar a situação (novo/alterado/igual).
  const [origem, setOrigem] = useState({ declarante: null, comprador: null, veiculo: null });
  const [salvandoDeclarante, setSalvandoDeclarante] = useState(false);
  const [salvandoComprador, setSalvandoComprador] = useState(false);
  const [salvandoVeiculo, setSalvandoVeiculo] = useState(false);
  const [buscandoCnpj, setBuscandoCnpj] = useState(null); // 'declarante' | 'comprador' | null
  const [confirmacaoCadastro, setConfirmacaoCadastro] = useState(null);

  const [documentoHtml, setDocumentoHtml] = useState('');
  const [versaoDocumento, setVersaoDocumento] = useState(0);
  const [mostrarPreview, setMostrarPreview] = useState(false);

  const [modalBuscaAberto, setModalBuscaAberto] = useState(false);
  const [buscaAlvo, setBuscaAlvo] = useState('declarante'); // 'declarante' | 'comprador'
  const [termoBusca, setTermoBusca] = useState('');
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(null); // 'declarante' | 'comprador' | null

  const [modalVeiculoAberto, setModalVeiculoAberto] = useState(false);
  const [placaBusca, setPlacaBusca] = useState('');
  const [buscandoPlaca, setBuscandoPlaca] = useState(false);
  const [resultadoPlaca, setResultadoPlaca] = useState(null);
  const [erroPlaca, setErroPlaca] = useState('');
  const [avisoVeiculo, setAvisoVeiculo] = useState('');

  // Cidade do escritório (vem no login): o documento sai "Canguçu, 21 de março de 2026"
  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    const dados = token ? decodeToken(token) : null;
    if (dados?.empresaCidade) setLocal((atual) => atual || dados.empresaCidade);
  }, []);

  // ── Busca de cliente (declarante e adquirente) ───────────────────────────
  const abrirBusca = (alvo) => {
    setBuscaAlvo(alvo);
    setTermoBusca('');
    setResultadosBusca([]);
    setModalBuscaAberto(true);
  };

  const executarBusca = async () => {
    const termo = termoBusca.trim();
    if (termo.length < 3) {
      addToast('warning', 'Informe ao menos 3 caracteres para pesquisar.');
      return;
    }
    setBuscando(true);
    setResultadosBusca([]);
    try {
      const doc = removerMascaraCPFCNPJ(termo);
      if (doc.length === 11 || doc.length === 14) {
        const cliente = await buscarClientePorDocumento(doc);
        if (cliente) {
          setResultadosBusca([cliente]);
          return;
        }
      }
      const respNome = await axios.get(`${API_BASE_URL}/clientes/nome/${encodeURIComponent(termo.toLowerCase())}`, getAuthHeader());
      if (respNome.data && respNome.data.length > 0) setResultadosBusca(respNome.data);
    } catch (error) {
      if (error.response?.status !== 404 && error.response?.status !== 204) {
        console.error('Erro na pesquisa de clientes:', error);
        addToast('error', 'Erro ao pesquisar clientes.');
      }
    } finally {
      setBuscando(false);
    }
  };

  const selecionarResultado = async (clienteResumo) => {
    setBuscando(true);
    try {
      const resp = await axios.get(`${API_BASE_URL}/clientes/${clienteResumo.idCliente}`, getAuthHeader());
      const pessoa = clienteParaPessoa(resp.data);
      if (buscaAlvo === 'declarante') {
        setDeclarante(pessoa);
        setErrosDeclarante({});
        setOrigem((o) => ({ ...o, declarante: pessoa }));
      } else {
        setComprador(pessoa);
        setErrosComprador({});
        setOrigem((o) => ({ ...o, comprador: pessoa }));
      }
      setModalBuscaAberto(false);
    } catch (error) {
      console.error('Erro ao carregar cliente:', error);
      addToast('error', 'Erro ao carregar dados do cliente.');
    } finally {
      setBuscando(false);
    }
  };

  const atualizarPessoa = (setPessoa, setErros) => (campo, valor) => {
    setPessoa((prev) => ({ ...prev, [campo]: valor }));
    setErros((prev) => ({ ...prev, [campo]: undefined }));
  };
  const atualizarDeclarante = atualizarPessoa(setDeclarante, setErrosDeclarante);
  const atualizarComprador = atualizarPessoa(setComprador, setErrosComprador);

  const mudarCep = (alvo, setPessoa) => async (valor) => {
    const formatado = formatarCep(valor);
    setPessoa((prev) => ({ ...prev, cep: formatado }));
    if (formatado.replace(/\D/g, '').length === 8) {
      setBuscandoCep(alvo);
      const dados = await buscarCep(formatado);
      if (dados) setPessoa((prev) => ({ ...prev, ...dados }));
      setBuscandoCep(null);
    }
  };

  // Lupa do CNPJ: preenche nome, contato e endereço pelos dados da Receita (mesma consulta do cadastro de clientes)
  const preencherPorCnpj = async (alvo, pessoa, setPessoa) => {
    setBuscandoCnpj(alvo);
    const { dados, erro } = await consultarCnpj(pessoa.cpfCnpj, API_BASE_URL, getAuthHeader());
    setBuscandoCnpj(null);
    if (!dados) {
      addToast('error', erro);
      return;
    }
    // Pessoa vinda do cadastro (cliente antigo/incompleto): só completa o que está em branco
    if (pessoa.idCliente) {
      const { novo, completados } = completarComDadosCnpj(pessoa, dados);
      novo.cep = formatarCep(novo.cep);
      setPessoa(novo);
      const aviso = avisoCompletarCnpj(completados);
      addToast(aviso.tipo, aviso.texto);
      return;
    }
    setPessoa((prev) => {
      const novo = { ...prev };
      Object.keys(dados).forEach((campo) => { novo[campo] = dados[campo] || ''; });
      novo.cep = formatarCep(novo.cep);
      return novo;
    });
    addToast('success', 'Dados preenchidos a partir do CNPJ. Confira antes de gerar o documento.');
  };

  // ── Veículo ──────────────────────────────────────────────────────────────

  // Documento importado (CRLV-e/ATPV/CRV): preenche os campos. O próprio botão já grava no cadastro de veículos.
  const aplicarDocumentoVeiculo = (dados) => {
    buscaPlacaRef.current++;
    const lido = veiculoDoCadastro({ ...dados, placa: (dados.placa || '').toUpperCase() });
    setVeiculo((prev) => {
      const novo = { ...prev };
      Object.keys(lido).forEach((campo) => { if (lido[campo]) novo[campo] = lido[campo]; });
      return novo;
    });
    setOrigem((o) => ({ ...o, veiculo: lido }));
    setAvisoVeiculo('');
    setErroVeiculo('');
  };

  const atualizarVeiculo = (campo, valor) => {
    setVeiculo((prev) => ({ ...prev, [campo]: valor }));
    setErroVeiculo('');
    // Placa completa digitada: procura no cadastro de veículos e preenche os dados gravados
    if (campo === 'placa') {
      const busca = ++buscaPlacaRef.current;
      setAvisoVeiculo('');
      if (!placaCompleta(valor)) return;
      buscarVeiculo(API_BASE_URL, { placa: valor }).then((v) => {
        if (busca !== buscaPlacaRef.current || !v) return;
        const dados = veiculoDoCadastro(v);
        setVeiculo((prev) => ({ ...prev, ...dados, placa: prev.placa }));
        setOrigem((o) => ({ ...o, veiculo: dados }));
        setAvisoVeiculo('Veículo encontrado no cadastro: dados preenchidos. Confira antes de gerar o documento.');
      });
    }
  };

  const abrirModalVeiculo = () => {
    setPlacaBusca('');
    setResultadoPlaca(null);
    setErroPlaca('');
    setModalVeiculoAberto(true);
  };

  const buscarVeiculoPorPlaca = async () => {
    const termo = placaBusca.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!termo) return;
    setBuscandoPlaca(true);
    setResultadoPlaca(null);
    setErroPlaca('');
    const v = await buscarVeiculo(API_BASE_URL, termo.length === 7 ? { placa: termo } : { chassi: termo });
    if (v) setResultadoPlaca(v);
    else setErroPlaca('Nenhum veículo cadastrado com essa placa/chassi.');
    setBuscandoPlaca(false);
  };

  const importarVeiculo = (v) => {
    buscaPlacaRef.current++;
    const dados = veiculoDoCadastro(v);
    setVeiculo(dados);
    setOrigem((o) => ({ ...o, veiculo: dados }));
    setErroVeiculo('');
    setAvisoVeiculo('');
    setModalVeiculoAberto(false);
    addToast('success', 'Dados do veículo importados.');
  };

  // Grava o veículo como está na tela (placa + chassi e o que mais estiver preenchido; em branco não apaga nada)
  const gravarVeiculo = async ({ avisar = false } = {}) => {
    if (!veiculo.placa.trim() && !veiculo.chassi.trim()) return false;
    const gravou = await salvarVeiculo(API_BASE_URL, veiculoParaCadastro(veiculo));
    if (gravou) {
      setOrigem((o) => {
        const anterior = o.veiculo || veiculoVazio();
        const atualizado = { ...anterior };
        CAMPOS_VEICULO_SYNC.forEach((c) => { if ((veiculo[c] || '').trim()) atualizado[c] = veiculo[c]; });
        return { ...o, veiculo: atualizado };
      });
    }
    if (avisar) {
      addToast(gravou ? 'success' : 'error', gravou ? 'Dados do veículo salvos no cadastro.' : 'Não foi possível salvar os dados do veículo.');
    }
    return gravou;
  };

  const salvarVeiculoNoCadastro = async () => {
    setSalvandoVeiculo(true);
    await gravarVeiculo({ avisar: true });
    setSalvandoVeiculo(false);
  };

  // ── Salvar/atualizar o cadastro de cliente a partir dos dados preenchidos aqui ──
  // Se já tem idCliente, atualiza só os campos preenchidos (nunca apaga o que já está no cadastro);
  // senão, verifica se já existe um cliente com o mesmo CPF/CNPJ (evita duplicar) e cria um novo caso não exista.
  const salvarClienteNoCadastro = (alvo) => {
    const dadosAlvo = alvo === 'declarante'
      ? { pessoa: declarante, setPessoa: setDeclarante, setSalvando: setSalvandoDeclarante, rotulo: 'declarante' }
      : { pessoa: comprador, setPessoa: setComprador, setSalvando: setSalvandoComprador, rotulo: 'adquirente' };
    const { pessoa, setPessoa, setSalvando, rotulo } = dadosAlvo;

    const complementar = async (idCliente, { silencioso = false } = {}) => {
      setSalvando(true);
      try {
        const salvo = await complementarCliente(idCliente, pessoa);
        const dados = clienteParaPessoa(salvo);
        setPessoa(dados);
        setOrigem((o) => ({ ...o, [alvo]: dados }));
        addToast('success', silencioso ? `Cadastro do ${rotulo} atualizado com os dados alterados.` : `Cadastro do ${rotulo} atualizado com sucesso!`);
      } catch (error) {
        console.error(`Erro ao atualizar cliente (${rotulo}):`, error);
        addToast('error', 'Erro ao salvar dados do cliente: ' + (error.response?.data?.message || error.message));
      } finally {
        setSalvando(false);
      }
    };

    const salvar = async () => {
      const doc = removerMascaraCPFCNPJ(pessoa.cpfCnpj);
      if (!pessoa.nome.trim() || (doc.length !== 11 && doc.length !== 14)) {
        addToast('warning', 'Preencha ao menos nome e CPF/CNPJ completo antes de salvar.');
        return;
      }
      if (pessoa.idCliente) {
        await complementar(pessoa.idCliente);
        return;
      }
      setSalvando(true);
      try {
        const existente = await buscarClientePorDocumento(doc);
        if (existente) {
          setSalvando(false);
          setConfirmacaoCadastro({
            titulo: 'Cliente já cadastrado',
            mensagem: `Já existe um cliente com este CPF/CNPJ: ${existente.nome}. Atualizar o cadastro dele com os dados preenchidos aqui?`,
            textoConfirmar: 'Atualizar',
            onConfirm: () => complementar(existente.idCliente)
          });
          return;
        }
        const salvo = await criarCliente(pessoa);
        const dados = clienteParaPessoa(salvo);
        setPessoa(dados);
        setOrigem((o) => ({ ...o, [alvo]: dados }));
        addToast('success', `${rotulo[0].toUpperCase()}${rotulo.slice(1)} cadastrado com sucesso!`);
      } catch (error) {
        console.error(`Erro ao cadastrar cliente (${rotulo}):`, error);
        addToast('error', 'Erro ao salvar dados do cliente: ' + (error.response?.data?.message || error.message));
      } finally {
        setSalvando(false);
      }
    };
    salvar.complementar = complementar;
    return salvar;
  };

  // ── Campos extras do modelo ──────────────────────────────────────────────
  const atualizarExtra = (chave, valor) => setExtras((prev) => ({ ...prev, [chave]: valor }));

  const alternarOpcao = (chave, valor) => {
    setExtras((prev) => {
      const atuais = prev[chave] || [];
      return { ...prev, [chave]: atuais.includes(valor) ? atuais.filter((x) => x !== valor) : [...atuais, valor] };
    });
  };

  const renderCampoExtra = (c) => {
    const valor = extras[c.chave];
    // Lista de itens (ex.: peças): uma linha por item, com + para abrir mais uma e × para remover
    if (c.tipo === 'lista') {
      const itens = Array.isArray(valor) && valor.length ? valor : [''];
      const mudarItem = (i, texto) => atualizarExtra(c.chave, itens.map((x, j) => (j === i ? texto : x)));
      const removerItem = (i) => atualizarExtra(c.chave, itens.length > 1 ? itens.filter((_, j) => j !== i) : ['']);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {itens.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ width: 22, textAlign: 'right', color: '#7f8c8d', fontSize: 13 }}>{i + 1}.</span>
              <Input
                style={{ flex: 1 }}
                value={item}
                onChange={(ev) => mudarItem(i, ev.target.value)}
                onKeyDown={(ev) => {
                  // Enter na última linha preenchida abre a próxima
                  if (ev.key === 'Enter' && i === itens.length - 1 && item.trim()) {
                    ev.preventDefault();
                    atualizarExtra(c.chave, [...itens, '']);
                  }
                }}
                placeholder={c.placeholder || ''}
                autoFocus={i > 0 && i === itens.length - 1 && !item}
              />
              <BotaoLista type="button" onClick={() => removerItem(i)} title="Remover" disabled={itens.length === 1 && !item}>
                <FaTimes />
              </BotaoLista>
            </div>
          ))}
          <div>
            <BotaoLista type="button" $adicionar onClick={() => atualizarExtra(c.chave, [...itens, ''])}>
              <FaPlus /> {c.rotuloAdicionar || 'Adicionar'}
            </BotaoLista>
          </div>
          <HintText>Itens em branco são ignorados. Sem nenhum item, o documento sai com linhas para preencher à mão.</HintText>
        </div>
      );
    }
    if (c.tipo === 'checks') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {c.opcoes.map((o) => (
            <label key={o.valor} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={(valor || []).includes(o.valor)} onChange={() => alternarOpcao(c.chave, o.valor)} />
              {o.rotulo}
            </label>
          ))}
        </div>
      );
    }
    if (c.tipo === 'select') {
      return (
        <SelectField style={{ width: '100%' }} value={valor} onChange={(ev) => atualizarExtra(c.chave, ev.target.value)}>
          {c.opcoes.map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
        </SelectField>
      );
    }
    if (c.tipo === 'valor') {
      return (
        <NumericFormat
          value={valor ?? ''}
          onValueChange={(v, info) => { if (info.source === 'event') atualizarExtra(c.chave, v.floatValue ?? null); }}
          thousandSeparator="." decimalSeparator="," decimalScale={2} fixedDecimalScale
          prefix="R$ " placeholder="R$ 0,00"
          customInput={Input}
        />
      );
    }
    return (
      <Input
        type={c.tipo === 'data' ? 'date' : (c.tipo === 'hora' ? 'time' : 'text')}
        value={valor}
        onChange={(ev) => atualizarExtra(c.chave, ev.target.value)}
        placeholder={c.placeholder || ''}
      />
    );
  };

  // ── Situação dos cadastros ────────────────────────────────────────────────
  const situacaoDeclarante = situacaoCadastro({
    vinculado: !!declarante.idCliente, origem: origem.declarante, atual: declarante, campos: CAMPOS_PESSOA,
    obrigatorios: ['nome', 'cpfCnpj'], rotuloNovo: 'Cliente ainda não cadastrado: será criado no cadastro.',
    camposFixos: ['cpfCnpj'], rotulosCampo: ROTULOS_CAMPO_PESSOA
  });
  const situacaoComprador = situacaoCadastro({
    vinculado: !!comprador.idCliente, origem: origem.comprador, atual: comprador, campos: CAMPOS_PESSOA,
    obrigatorios: ['nome', 'cpfCnpj'], rotuloNovo: 'Cliente ainda não cadastrado: será criado no cadastro.',
    camposFixos: ['cpfCnpj'], rotulosCampo: ROTULOS_CAMPO_PESSOA
  });
  const situacaoVeiculo = situacaoCadastro({
    vinculado: !!origem.veiculo, origem: origem.veiculo, atual: veiculo, campos: CAMPOS_VEICULO_SYNC,
    obrigatorios: ['placa', 'chassi'], rotuloNovo: 'Veículo ainda não cadastrado: será gravado ao gerar o documento.',
    rotulosCampo: ROTULOS_CAMPO_VEICULO
  });

  const salvarDeclarante = salvarClienteNoCadastro('declarante');
  const salvarComprador = salvarClienteNoCadastro('comprador');

  // ── Gerar / imprimir ─────────────────────────────────────────────────────
  const validar = () => {
    const erros = {};
    if (!declarante.nome.trim()) erros.nome = 'Informe o nome.';
    if (!declarante.cpfCnpj.trim()) erros.cpfCnpj = 'Informe o CPF/CNPJ.';
    setErrosDeclarante(erros);

    const temVeiculo = veiculo.placa.trim() || veiculo.chassi.trim();
    setErroVeiculo(temVeiculo ? '' : 'Informe ao menos a placa ou o chassi do veículo.');

    const errosComp = {};
    if (modelo.comprador) {
      if (!comprador.nome.trim()) errosComp.nome = 'Informe o nome do adquirente.';
      if (!comprador.cpfCnpj.trim()) errosComp.cpfCnpj = 'Informe o CPF/CNPJ do adquirente.';
    }
    setErrosComprador(errosComp);

    const ok = Object.keys(erros).length === 0 && !!temVeiculo && Object.keys(errosComp).length === 0;
    if (!ok) addToast('warning', 'Preencha os campos obrigatórios destacados em vermelho.');
    return ok;
  };

  // Ao gerar/imprimir: grava o veículo e atualiza os clientes já cadastrados que foram alterados aqui.
  // Cliente novo (sem cadastro) só é criado pelo botão "Salvar dados do cliente". Nunca bloqueia o documento.
  const atualizarCadastros = async () => {
    const tarefas = [gravarVeiculo()];
    if (situacaoDeclarante.tipo === 'alterado') tarefas.push(salvarDeclarante.complementar(declarante.idCliente, { silencioso: true }));
    if (modelo.comprador && situacaoComprador.tipo === 'alterado') tarefas.push(salvarComprador.complementar(comprador.idCliente, { silencioso: true }));
    await Promise.all(tarefas);
  };

  const montarHtml = () => gerarDocumentoHtml(modelo, {
    declarante, comprador, veiculo, extras, local, dataISO: dataDocumento
  });

  const gerarDocumento = async () => {
    if (!validar()) return;
    if (mostrarPreview && editadoRef.current &&
        !window.confirm('Gerar novamente vai descartar as alterações feitas no documento. Continuar?')) {
      return;
    }
    setDocumentoHtml(sanitizarHtml(montarHtml()));
    setVersaoDocumento((v) => v + 1);
    editadoRef.current = false;
    setMostrarPreview(true);
    setTimeout(() => {
      if (previewRef.current) previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    await atualizarCadastros();
  };

  const imprimir = async () => {
    await gravarVeiculo();
    window.print();
  };

  const restaurarOriginal = () => {
    if (!window.confirm('Descartar as alterações e voltar ao texto original?')) return;
    setDocumentoHtml(sanitizarHtml(montarHtml()));
    setVersaoDocumento((v) => v + 1);
    editadoRef.current = false;
  };

  const novoDocumento = () => {
    buscaPlacaRef.current++;
    setDeclarante(pessoaVazia());
    setComprador(pessoaVazia());
    setVeiculo(veiculoVazio());
    setOrigem({ declarante: null, comprador: null, veiculo: null });
    setExtras(extrasIniciais(modelo));
    setDataDocumento('');
    setErrosDeclarante({});
    setErrosComprador({});
    setErroVeiculo('');
    setAvisoVeiculo('');
    setMostrarPreview(false);
  };

  // Comandos de formatação do editor (estilo Word); onMouseDown + preventDefault mantém a seleção
  const comando = (nome, valor) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(nome, false, valor);
  };
  const botaoEditor = (nome, valor) => ({
    type: 'button',
    onMouseDown: (ev) => { ev.preventDefault(); comando(nome, valor); }
  });

  // Colar sempre como texto puro, para não trazer fontes e cores de outros documentos
  const colarTextoPuro = (ev) => {
    ev.preventDefault();
    const texto = (ev.clipboardData || window.clipboardData).getData('text');
    document.execCommand('insertText', false, texto);
  };

  const pessoaCampos = (pessoa, atualizar, erros, alvo, setPessoa, situacao, salvando, onSalvar) => {
    const ehCnpj = removerMascaraCPFCNPJ(pessoa.cpfCnpj).length === 14;
    const podeConsultarCnpj = ehCnpj && buscandoCnpj === null;
    return (
      <>
        <FormRow>
          <InputGroup style={{ flex: 2 }}>
            <Label>Nome*</Label>
            <Input value={pessoa.nome} onChange={(ev) => atualizar('nome', ev.target.value)} placeholder="Nome completo" hasError={!!erros.nome} />
            {erros.nome && <ErrorText>{erros.nome}</ErrorText>}
          </InputGroup>
          <InputGroup>
            <Label>CPF/CNPJ*</Label>
            <div style={{ display: 'flex', gap: 6 }}>
              <Input
                style={{ flex: 1, minWidth: 0 }}
                value={mascaraCpfCnpj(pessoa.cpfCnpj)}
                onChange={(ev) => atualizar('cpfCnpj', removerMascaraCPFCNPJ(ev.target.value).slice(0, 14))}
                placeholder="000.000.000-00"
                hasError={!!erros.cpfCnpj}
                disabled={!!pessoa.idCliente}
              />
              <LupaCnpj
                type="button"
                onClick={() => preencherPorCnpj(alvo, pessoa, setPessoa)}
                disabled={!podeConsultarCnpj}
                title={podeConsultarCnpj
                  ? (pessoa.idCliente ? 'Completar os campos em branco com os dados do CNPJ (Receita Federal)' : 'Buscar dados do CNPJ (Receita Federal)')
                  : 'Digite um CNPJ completo para buscar os dados'}
              >
                {buscandoCnpj === alvo ? <FaSpinner className="spin" /> : <FaSearch />}
              </LupaCnpj>
            </div>
            {erros.cpfCnpj && <ErrorText>{erros.cpfCnpj}</ErrorText>}
            {buscandoCnpj === alvo && <HintText>Consultando CNPJ...</HintText>}
            {pessoa.idCliente && <HintText>Cliente do cadastro: o CPF/CNPJ não pode ser alterado aqui.</HintText>}
          </InputGroup>
        </FormRow>
        <FormRow>
          <InputGroup>
            <Label>RG</Label>
            <Input value={pessoa.rg} onChange={(ev) => atualizar('rg', ev.target.value.toUpperCase())} placeholder="0000000000" />
          </InputGroup>
          <InputGroup>
            <Label>Telefone</Label>
            <Input value={pessoa.telefone} onChange={(ev) => atualizar('telefone', ev.target.value)} placeholder="(53) 99999-9999" />
          </InputGroup>
          <InputGroup>
            <Label>E-mail</Label>
            <Input value={pessoa.email} onChange={(ev) => atualizar('email', ev.target.value)} placeholder="email@exemplo.com" />
          </InputGroup>
        </FormRow>
        <SubTitle><FaMapMarkerAlt size={12} /> Endereço</SubTitle>
        <EnderecoCampos
          valor={pessoa}
          onChange={atualizar}
          onCepChange={mudarCep(alvo, setPessoa)}
          buscando={buscandoCep === alvo}
        />
        <BarraCadastro situacao={situacao} salvando={salvando} onSalvar={onSalvar} rotulo="Salvar dados do cliente" />
      </>
    );
  };

  return (
    <PageBackground className="docc-page">
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          body * { visibility: hidden; }
          .docc-print-area, .docc-print-area * { visibility: visible; }
          .no-print { display: none !important; }
          .docc-page { min-height: 0 !important; background: none !important; }
          .docc-preview { margin: 0 !important; padding: 0 !important; max-width: none !important; }
          .docc-print-area {
            box-shadow: none !important; border: none !important; border-radius: 0 !important;
            padding: 16mm 28mm !important; box-sizing: border-box; width: 100%;
          }
        }
      `}</style>

      <div className="no-print">
        <Header />
      </div>

      <Content className="no-print">
        <TitleRow>
          <Title><FaFileAlt size={22} /> {modelo.titulo}</Title>
          <SecondaryButton onClick={() => navigate('/documentos-complementares')}>
            <FaArrowLeft /> Documentos
          </SecondaryButton>
        </TitleRow>

        {/* ── Declarante / proprietário ─────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle><FaUser size={16} /> {modelo.rotuloDeclarante || (modelo.comprador ? 'Proprietário vendedor' : 'Declarante / proprietário')}</CardTitle>
            <SearchButton onClick={() => abrirBusca('declarante')}>
              <FaSearch size={12} /> Buscar Cliente Cadastrado
            </SearchButton>
          </CardHeader>
          {pessoaCampos(declarante, atualizarDeclarante, errosDeclarante, 'declarante', setDeclarante, situacaoDeclarante, salvandoDeclarante, salvarDeclarante)}
        </Card>

        {/* ── Adquirente (só nos documentos de venda) ───────────────────── */}
        {modelo.comprador && (
          <Card>
            <CardHeader>
              <CardTitle><FaUser size={16} /> Adquirente (comprador)</CardTitle>
              <SearchButton onClick={() => abrirBusca('comprador')}>
                <FaSearch size={12} /> Buscar Cliente Cadastrado
              </SearchButton>
            </CardHeader>
            {pessoaCampos(comprador, atualizarComprador, errosComprador, 'comprador', setComprador, situacaoComprador, salvandoComprador, salvarComprador)}
          </Card>
        )}

        {/* ── Veículo ────────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle><FaCar size={16} /> Veículo</CardTitle>
            <SearchButton onClick={abrirModalVeiculo}>
              <FaSearch size={12} /> Selecionar Veículo Cadastrado
            </SearchButton>
          </CardHeader>
          <ImportarDocumentoVeiculo onDadosLidos={aplicarDocumentoVeiculo} />
          {avisoVeiculo && <HintText style={{ color: '#27ae60', display: 'block', marginBottom: 8 }}>{avisoVeiculo}</HintText>}
          <FormRow>
            <InputGroup>
              <Label>Placa</Label>
              <Input value={veiculo.placa} onChange={(ev) => atualizarVeiculo('placa', ev.target.value.toUpperCase())} placeholder="AAA0A00" maxLength={8} hasError={!!erroVeiculo} />
            </InputGroup>
            <InputGroup style={{ flex: 2 }}>
              <Label>Chassi</Label>
              <Input value={veiculo.chassi} onChange={(ev) => atualizarVeiculo('chassi', ev.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} placeholder="17 caracteres" maxLength={20} hasError={!!erroVeiculo} />
            </InputGroup>
            <InputGroup>
              <Label>RENAVAM</Label>
              <Input value={veiculo.renavam} onChange={(ev) => atualizarVeiculo('renavam', ev.target.value.replace(/\D/g, ''))} maxLength={11} />
            </InputGroup>
            <InputGroup>
              <Label>Nº do motor</Label>
              <Input value={veiculo.motor} onChange={(ev) => atualizarVeiculo('motor', ev.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} placeholder="Número gravado no motor" maxLength={25} />
            </InputGroup>
          </FormRow>
          {erroVeiculo && <ErrorText>{erroVeiculo}</ErrorText>}
          <FormRow>
            <InputGroup style={{ flex: 2 }}>
              <Label>Marca/Modelo</Label>
              <Input value={veiculo.marcaModelo} onChange={(ev) => atualizarVeiculo('marcaModelo', ev.target.value.toUpperCase())} placeholder="Ex.: HONDA/CG 160 START" />
            </InputGroup>
            <InputGroup>
              <Label>Cor</Label>
              <Input value={veiculo.cor} onChange={(ev) => atualizarVeiculo('cor', ev.target.value.toUpperCase())} />
            </InputGroup>
            <InputGroup>
              <Label>Ano fab./modelo</Label>
              <Input value={veiculo.ano} onChange={(ev) => atualizarVeiculo('ano', ev.target.value)} placeholder="2015/2016" maxLength={9} />
            </InputGroup>
          </FormRow>

          <BarraCadastro
            situacao={situacaoVeiculo} salvando={salvandoVeiculo} onSalvar={salvarVeiculoNoCadastro}
            rotulo="Salvar dados do veículo"
          />
        </Card>

        {/* ── Campos próprios do documento ──────────────────────────────── */}
        {modelo.campos.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle><FaFileAlt size={16} /> Dados do documento</CardTitle>
            </CardHeader>
            {modelo.campos.map((c) => (
              <FormRow key={c.chave}>
                <InputGroup>
                  <Label>{c.rotulo}</Label>
                  {renderCampoExtra(c)}
                </InputGroup>
              </FormRow>
            ))}
          </Card>
        )}

        {/* ── Local e data ───────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Local e data</CardTitle>
          </CardHeader>
          <FormRow>
            <InputGroup>
              <Label>Local</Label>
              <Input value={local} onChange={(ev) => setLocal(ev.target.value)} placeholder="Canguçu-RS" />
            </InputGroup>
            <InputGroup>
              <Label>Data do documento</Label>
              <Input type="date" value={dataDocumento} onChange={(ev) => setDataDocumento(ev.target.value)} />
            </InputGroup>
          </FormRow>
          <HintText>Sem data, o documento sai com o espaço em branco para preencher à mão.</HintText>
        </Card>

        <ActionsRow>
          <PrimaryButton onClick={gerarDocumento}>
            <FaCheckCircle /> Gerar Documento
          </PrimaryButton>
          {mostrarPreview && (
            <SecondaryButton onClick={novoDocumento}>
              <FaArrowLeft /> Novo Documento
            </SecondaryButton>
          )}
        </ActionsRow>
      </Content>

      {/* ── Documento (preview + área de impressão) ──────────────────────── */}
      {mostrarPreview && (
        <PreviewWrapper ref={previewRef} className="docc-preview">
          <div className="no-print">
            <PreviewToolbar>
              <span>Pré-visualização — clique no texto para editar. O documento não é salvo no sistema.</span>
              <PrintButton onClick={imprimir}>
                <FaPrint /> Imprimir / Salvar PDF
              </PrintButton>
            </PreviewToolbar>
            <EditorToolbar>
              <FerramentaBtn {...botaoEditor('bold')} title="Negrito (Ctrl+B)"><FaBold /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('italic')} title="Itálico (Ctrl+I)"><FaItalic /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('underline')} title="Sublinhado (Ctrl+U)"><FaUnderline /></FerramentaBtn>
              <Separador />
              <FerramentaBtn {...botaoEditor('justifyLeft')} title="Alinhar à esquerda"><FaAlignLeft /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('justifyCenter')} title="Centralizar"><FaAlignCenter /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('justifyRight')} title="Alinhar à direita"><FaAlignRight /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('justifyFull')} title="Justificar"><FaAlignJustify /></FerramentaBtn>
              <Separador />
              <FerramentaBtn {...botaoEditor('undo')} title="Desfazer (Ctrl+Z)"><FaUndo /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('redo')} title="Refazer (Ctrl+Y)"><FaRedo /></FerramentaBtn>
              <Separador />
              <FerramentaBtn
                type="button"
                onClick={restaurarOriginal}
                title="Descartar alterações e voltar ao texto original"
                style={{ width: 'auto', padding: '0 10px', gap: 6 }}
              >
                <FaEraser /> Restaurar original
              </FerramentaBtn>
            </EditorToolbar>
          </div>

          <Folha
            key={versaoDocumento}
            ref={editorRef}
            className="docc-print-area"
            contentEditable
            suppressContentEditableWarning
            spellCheck
            onPaste={colarTextoPuro}
            onInput={() => { editadoRef.current = true; }}
            dangerouslySetInnerHTML={{ __html: documentoHtml }}
          />
        </PreviewWrapper>
      )}

      {/* ── Modal de busca de cliente ────────────────────────────────────── */}
      {modalBuscaAberto && (
        <ModalOverlay className="no-print">
          <ModalBox>
            <ModalHeader>
              <h3>Buscar Cliente Cadastrado</h3>
              <ModalClose onClick={() => setModalBuscaAberto(false)}><FaTimes /></ModalClose>
            </ModalHeader>
            <ModalContent>
              <SearchRow>
                <Input
                  value={termoBusca}
                  onChange={(ev) => setTermoBusca(ev.target.value)}
                  placeholder="Nome ou CPF/CNPJ"
                  onKeyPress={(ev) => ev.key === 'Enter' && executarBusca()}
                  autoFocus
                />
                <PrimaryButton onClick={executarBusca} disabled={buscando}>
                  <FaSearch /> {buscando ? 'Buscando...' : 'Buscar'}
                </PrimaryButton>
              </SearchRow>

              {resultadosBusca.length > 0 ? (
                <ResultList>
                  {resultadosBusca.map((c) => (
                    <ResultItem key={c.idCliente} onClick={() => selecionarResultado(c)}>
                      <strong>{c.nome}</strong>
                      <span>{formatarCpfCnpj(c.cpf || c.cnpj) || 'Sem CPF/CNPJ'}</span>
                    </ResultItem>
                  ))}
                </ResultList>
              ) : (
                <EmptyState>Nenhum resultado. Digite um nome ou CPF/CNPJ e busque.</EmptyState>
              )}
            </ModalContent>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── Modal de seleção de veículo (por placa ou chassi) ────────────── */}
      {modalVeiculoAberto && (
        <ModalOverlay className="no-print">
          <ModalBox>
            <ModalHeader>
              <h3>Selecionar Veículo</h3>
              <ModalClose onClick={() => setModalVeiculoAberto(false)}><FaTimes /></ModalClose>
            </ModalHeader>
            <ModalContent>
              <SubTitle>Buscar veículo cadastrado por placa ou chassi</SubTitle>
              <SearchRow>
                <Input
                  value={placaBusca}
                  onChange={(ev) => setPlacaBusca(ev.target.value.toUpperCase())}
                  placeholder="AAA0A00 ou chassi"
                  onKeyPress={(ev) => ev.key === 'Enter' && buscarVeiculoPorPlaca()}
                  autoFocus
                />
                <PrimaryButton onClick={buscarVeiculoPorPlaca} disabled={buscandoPlaca}>
                  <FaSearch /> {buscandoPlaca ? 'Buscando...' : 'Buscar'}
                </PrimaryButton>
              </SearchRow>

              {erroPlaca && <EmptyState>{erroPlaca}</EmptyState>}

              {resultadoPlaca && (
                <ResultList>
                  <ResultItem onClick={() => importarVeiculo(resultadoPlaca)}>
                    <strong>{resultadoPlaca.marcaModelo || 'Veículo'} — {resultadoPlaca.placa || resultadoPlaca.chassi}</strong>
                    <span>{resultadoPlaca.chassi ? `Chassi ${resultadoPlaca.chassi} · ` : ''}Clique para importar os dados</span>
                  </ResultItem>
                </ResultList>
              )}
            </ModalContent>
          </ModalBox>
        </ModalOverlay>
      )}

      <ConfirmModal
        isOpen={!!confirmacaoCadastro}
        onClose={() => setConfirmacaoCadastro(null)}
        onConfirm={() => { if (confirmacaoCadastro) confirmacaoCadastro.onConfirm(); }}
        title={confirmacaoCadastro ? confirmacaoCadastro.titulo : ''}
        message={confirmacaoCadastro ? confirmacaoCadastro.mensagem : ''}
        confirmText={confirmacaoCadastro ? confirmacaoCadastro.textoConfirmar : 'Confirmar'}
        cancelText="Cancelar"
      />
    </PageBackground>
  );
};

const BotaoLista = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  padding: ${(p) => (p.$adicionar ? '7px 14px' : '8px 10px')};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$adicionar ? '#008080' : 'white')};
  color: ${(p) => (p.$adicionar ? 'white' : '#c0392b')};
  border: 1px solid ${(p) => (p.$adicionar ? '#008080' : '#e6b0aa')};
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const LupaCnpj = styled.button`
  background: #008080;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  cursor: pointer;
  &:disabled { background: #b8c2cc; cursor: not-allowed; }
`;

// Folha A4 + estilos próprios dos requerimentos (campos, opções, caixa de declaração)
const Folha = styled(PaperSheet)`
  h1.longo { font-size: 13pt; }
  .secao { font-weight: 700; margin: 16px 0 6px 0; }
  .campo { margin: 4px 0; }
  .opcao { margin: 3px 0 3px 24px; }
  .branco { display: inline-block; min-width: 230px; border-bottom: 1px solid #333; height: 1em; }
  .caixa { border: 1px solid #333; background: #f2f2f2; padding: 8px 10px; text-align: justify; margin: 14px 0; }
  .fecha .nota { font-size: 9pt; margin: 10px 0 0 0; text-align: justify; }
`;

export default DocumentosComplementares;
